import {isNotNil, omit} from 'ramda';

import type {CarFeaturesResponseBody} from '@dms/api/core';
import {VehicleTypeEnumObject} from '@dms/api/shared';
import type {
  ColorRequestBody,
  ConditionEnum,
  GetVehicleApiResponse,
  UpdateVehicleRequestBody,
  VehicleFeatureV2ResponseBody,
  VehicleTypeEnum,
} from '@dms/api/vehicle';

import {isElectricOrHybridFuelType} from '../../kit/utils/isElectricOrHybridFuelType';
import {MAKE_OTHER, MODEL_FAMILY_OTHER, MODEL_FAMILY_OTHER_END} from '../constants/makeModel';
import {BaseVehicle} from '../types/BaseVehicle';
import {SaleVehicle} from '../types/SaleVehicle';
import {createCustomFields} from '../utils/createCustomFields';

/**
 * Helper function to format color data for the API from either a string or a color object.
 */
const formatColor = (
  color: BaseVehicle['exteriorColor'] | BaseVehicle['interiorColor']
): ColorRequestBody | null => {
  if (!color) {
    return null;
  }

  // If the form sends a simple string, convert it to the object format the API expects
  if (typeof color === 'string') {
    return {value: color, name: color, code: color};
  }

  // If it's already an object, pass it through
  return color;
};

/**
 * Helper function to determine the correct modelFamily value based on custom make/model logic
 */
const getModelFamilyValue = (formData: BaseVehicle): string => {
  if (!formData.customModelFamily || formData.customModelFamily !== formData.formModelFamily) {
    return formData.formModelFamily ?? formData.modelFamily ?? '';
  }

  if (formData.customMake && formData.formMake && formData.customMake === formData.formMake) {
    return MODEL_FAMILY_OTHER;
  }

  const makeValue = formData.formMake ?? formData.make ?? '';
  return `${makeValue}${MODEL_FAMILY_OTHER_END}`;
};

/**
 * Helper function to determine feature highlighting and premium status
 */
const mapFeatures = (
  features: string[] | undefined,
  existingFeatures?: VehicleFeatureV2ResponseBody[] | null,
  sourcingData?: CarFeaturesResponseBody | null
) => {
  if (!features) {
    return [];
  }

  return features.map((key: string) => {
    // Check for existing feature with override highlighting (like in RentalVehicleForm)
    const existingFeature = existingFeatures?.find((feature) => feature.key === key);

    // Determine highlighting: use existing override or sourcing data
    const isHighlighted = existingFeature
      ? existingFeature.isHighlighted
      : (sourcingData?.highlightedFeatures?.includes(key) ?? false);

    // Determine premium status from sourcing data
    const isPremium = sourcingData?.premiumFeatures?.includes(key) ?? false;

    return {
      key,
      isHighlighted,
      isPremium,
    };
  });
};

/**
 * Converts unified form data to V2 base vehicle API payload
 * This handles the common fields shared across all vehicle types
 *
 * @param formData - The form data to convert
 * @param existingFeatures - Existing vehicle features with current highlight/premium status
 * @param sourcingData - Feature data from sourcing API containing highlighted and premium feature arrays
 * @param existingVehicleData - Existing(previous) vehicle data for reference purposes - currently
 * this is only needed because of customFields, where updating behaves like PATCH
 */
export const toAPIVehicle = (
  formData: BaseVehicle | SaleVehicle,
  existingFeatures?: VehicleFeatureV2ResponseBody[] | null,
  sourcingData?: CarFeaturesResponseBody | null,
  existingVehicleData?: GetVehicleApiResponse
): UpdateVehicleRequestBody => {
  const isElectricOrHybrid = isElectricOrHybridFuelType(formData.fuelType?.at(0));

  return {
    ...omit(
      [
        'unknownMakes',
        'unknownModels',
        'formMake',
        'formModelFamily',
        'additionalNote',
        'auditData',
        'auditAssets',
        'sessionId',
        'batteryCertificateDocument',
      ],
      formData
    ),
    // Basic Information

    axleCount: formData.axleCount?.at(0) ? Number(formData.axleCount.at(0)) : null,
    registrationPlate: formData.registrationPlate?.toUpperCase() || null,
    vin: formData.vin?.toUpperCase() || null,
    type:
      (formData.type?.at(0) as VehicleTypeEnum) || VehicleTypeEnumObject.VEHICLETYPE_PASSENGER_CAR,

    make:
      formData.customMake && formData.formMake && formData.customMake === formData.formMake
        ? MAKE_OTHER
        : (formData.formMake ?? formData.make ?? ''),
    customMake:
      formData.customMake && formData.formMake && formData.customMake === formData.formMake
        ? formData.customMake
        : null,
    modelFamily: getModelFamilyValue(formData),
    customModelFamily:
      formData.customModelFamily &&
      formData.formModelFamily &&
      formData.customModelFamily === formData.formModelFamily
        ? formData.customModelFamily
        : null,

    // German-specific fields as customFields
    customFields: createCustomFields(formData, existingVehicleData),

    // Vehicle Details
    trim: formData.trim || null,
    variant: formData.variant || null,
    title: formData.title || null,
    firstRegistrationOn: formData.firstRegistrationOn || null,

    // Consumption & Emissions
    // NEDC consumption fields
    fuelConsumptionCombined: isNotNil(formData.fuelConsumptionCombined)
      ? String(formData.fuelConsumptionCombined)
      : null,
    fuelConsumptionExtraUrban: isNotNil(formData.fuelConsumptionExtraUrban)
      ? String(formData.fuelConsumptionExtraUrban)
      : null,
    fuelConsumptionUrban: isNotNil(formData.fuelConsumptionUrban)
      ? String(formData.fuelConsumptionUrban)
      : null,
    // WLTP consumption fields (liquid/gas)
    fuelConsumptionWltpCombined: isNotNil(formData.fuelConsumptionWltpCombined)
      ? String(formData.fuelConsumptionWltpCombined)
      : null,
    fuelConsumptionWltpCityCenter: isNotNil(formData.fuelConsumptionWltpCityCenter)
      ? String(formData.fuelConsumptionWltpCityCenter)
      : null,
    fuelConsumptionWltpOutskirts: isNotNil(formData.fuelConsumptionWltpOutskirts)
      ? String(formData.fuelConsumptionWltpOutskirts)
      : null,
    fuelConsumptionWltpCountryRoads: isNotNil(formData.fuelConsumptionWltpCountryRoads)
      ? String(formData.fuelConsumptionWltpCountryRoads)
      : null,
    fuelConsumptionWltpHighway: isNotNil(formData.fuelConsumptionWltpHighway)
      ? String(formData.fuelConsumptionWltpHighway)
      : null,
    fuelConsumptionWltpWeighted: isNotNil(formData.fuelConsumptionWltpWeighted)
      ? String(formData.fuelConsumptionWltpWeighted)
      : null,
    // WLTP consumption fields (electric)
    fuelConsumptionWltpCombinedKwh: isNotNil(formData.fuelConsumptionWltpCombinedKwh)
      ? String(formData.fuelConsumptionWltpCombinedKwh)
      : null,
    fuelConsumptionWltpCityCenterKwh: isNotNil(formData.fuelConsumptionWltpCityCenterKwh)
      ? String(formData.fuelConsumptionWltpCityCenterKwh)
      : null,
    fuelConsumptionWltpOutskirtsKwh: isNotNil(formData.fuelConsumptionWltpOutskirtsKwh)
      ? String(formData.fuelConsumptionWltpOutskirtsKwh)
      : null,
    fuelConsumptionWltpCountryRoadsKwh: isNotNil(formData.fuelConsumptionWltpCountryRoadsKwh)
      ? String(formData.fuelConsumptionWltpCountryRoadsKwh)
      : null,
    fuelConsumptionWltpHighwayKwh: isNotNil(formData.fuelConsumptionWltpHighwayKwh)
      ? String(formData.fuelConsumptionWltpHighwayKwh)
      : null,
    fuelConsumptionWltpWeightedCombinedKwh: isNotNil(
      formData.fuelConsumptionWltpWeightedCombinedKwh
    )
      ? String(formData.fuelConsumptionWltpWeightedCombinedKwh)
      : null,
    carbonDioxideEmission: isNotNil(formData.carbonDioxideEmission)
      ? String(formData.carbonDioxideEmission)
      : null,
    carbonDioxideEmissionDischarged: isNotNil(formData.carbonDioxideEmissionDischarged)
      ? String(formData.carbonDioxideEmissionDischarged)
      : null,
    carbonDioxideEmissionWeightedCombined: isNotNil(formData.carbonDioxideEmissionWeightedCombined)
      ? String(formData.carbonDioxideEmissionWeightedCombined)
      : null,
    emissionClass: formData.emissionClass || null,
    carbonDioxideEmissionClass: formData.carbonDioxideEmissionClass || null,
    carbonDioxideEmissionClassDischarged: formData.carbonDioxideEmissionClassDischarged || null,
    carbonDioxideEmissionClassWeightedCombined:
      formData.carbonDioxideEmissionClassWeightedCombined || null,

    // Manufactured date - convert from separate year/month to ISO date
    manufacturedOn: formatManufacturedDate(
      formData.manufacturedOnYear,
      formData.manufacturedOnMonth
    ),

    // Technical Specs - convert arrays to single values
    bodyStyle: formData.bodyStyle?.at(0) || null,
    doorCountCategory: formData.doorCountCategory?.at(0) || null,
    seatCount: formData.seatCount?.at(0) ? Number(formData.seatCount.at(0)) : null,
    fuelType: formData.fuelType?.at(0) || null,
    secondaryFuelType: formData.secondaryFuelType?.at(0) || null,
    hybridType: formData.hybridType?.at(0) || null,
    transmission: formData.transmission?.at(0) || null,
    drive: formData.drive?.at(0) || null,
    dimensions: formData.dimensions
      ? {
          width: isNotNil(formData.dimensions.width) ? String(formData.dimensions.width) : null,
          height: isNotNil(formData.dimensions.height) ? String(formData.dimensions.height) : null,
          length: isNotNil(formData.dimensions.length) ? String(formData.dimensions.length) : null,
        }
      : null,
    dimensionsLoadingSpace: formData.dimensionsLoadingSpace
      ? {
          width: isNotNil(formData.dimensionsLoadingSpace.width)
            ? String(formData.dimensionsLoadingSpace.width)
            : null,
          height: isNotNil(formData.dimensionsLoadingSpace.height)
            ? String(formData.dimensionsLoadingSpace.height)
            : null,
          length: isNotNil(formData.dimensionsLoadingSpace.length)
            ? String(formData.dimensionsLoadingSpace.length)
            : null,
        }
      : null,
    weight: isNotNil(formData.weight) ? Number(formData.weight) : null,
    operatingWeight: isNotNil(formData.operatingWeight) ? Number(formData.operatingWeight) : null,
    loadingCapacity: isNotNil(formData.loadingCapacity) ? Number(formData.loadingCapacity) : null,
    trunkSpace: isNotNil(formData.trunkSpace) ? Number(formData.trunkSpace) : null,
    wheelBase: isNotNil(formData.wheelBase) ? Number(formData.wheelBase) : null,

    // Engine
    power: isNotNil(formData.power) ? Number(formData.power) : null,
    engineCode: formData.engineCode || null,
    engineName: formData.engineName || null,
    engineNumber: formData.engineNumber || null,
    engineVolume: isNotNil(formData.engineVolume) ? Number(formData.engineVolume) : null,
    maxTorque: formData.maxTorque ? String(formData.maxTorque) : null,
    electricRange: isNotNil(formData.electricRange) ? Number(formData.electricRange) : null,

    // Electric Vehicle Battery - structured based on fuel type
    electricVehicleBattery: isElectricOrHybrid
      ? {
          chargingAC: formData.electricVehicleBattery?.chargingAC
            ? String(formData.electricVehicleBattery.chargingAC)
            : null,
          chargingDC: formData.electricVehicleBattery?.chargingDC
            ? String(formData.electricVehicleBattery.chargingDC)
            : null,
          batteryCapacityWh: formData.electricVehicleBattery?.batteryCapacityWh
            ? String(formData.electricVehicleBattery.batteryCapacityWh)
            : null,
          batteryCapacityAh: formData.electricVehicleBattery?.batteryCapacityAh
            ? String(formData.electricVehicleBattery.batteryCapacityAh)
            : null,
          batteryVoltage: formData.electricVehicleBattery?.batteryVoltage
            ? String(formData.electricVehicleBattery.batteryVoltage)
            : null,
        }
      : null,

    // Top-level battery fields - always sent as integers regardless of vehicle type
    // For electric/hybrid vehicles: if electricVehicleBattery has values, set top-level fields to null
    // to avoid duplication (values are already in the nested electricVehicleBattery object as strings).
    // This separation exists because nested values can be decimals, while top-level fields must be integers.
    // For other vehicles: use top-level form fields
    batteryCapacityWh: isNotNil(formData.electricVehicleBattery?.batteryCapacityWh)
      ? null
      : isNotNil(formData.batteryCapacityWh)
        ? Number(formData.batteryCapacityWh)
        : null,
    batteryCapacityAh: isNotNil(formData.electricVehicleBattery?.batteryCapacityAh)
      ? null
      : isNotNil(formData.batteryCapacityAh)
        ? Number(formData.batteryCapacityAh)
        : null,
    batteryVoltage: isNotNil(formData.electricVehicleBattery?.batteryVoltage)
      ? null
      : isNotNil(formData.batteryVoltage)
        ? Number(formData.batteryVoltage)
        : null,
    batteryCount: isNotNil(formData.batteryCount) ? Number(formData.batteryCount) : null,

    // Top-level charging fields - always sent as integers regardless of vehicle type
    // For electric/hybrid vehicles: if electricVehicleBattery has values, set top-level fields to null
    // to avoid duplication (values are already in the nested electricVehicleBattery object as strings).
    // This separation exists because nested values can be decimals, while top-level fields must be integers.
    // For other vehicles: use top-level form fields
    chargingAC: isNotNil(formData.electricVehicleBattery?.chargingAC)
      ? null
      : isNotNil(formData.chargingAC)
        ? Number(formData.chargingAC)
        : null,
    chargingDC: isNotNil(formData.electricVehicleBattery?.chargingDC)
      ? null
      : isNotNil(formData.chargingDC)
        ? Number(formData.chargingDC)
        : null,

    // Other electric/battery related fields
    chargingTimeAC: isNotNil(formData.chargingTimeAC) ? Number(formData.chargingTimeAC) : null,
    chargingTimeDC: isNotNil(formData.chargingTimeDC)
      ? Math.round(Number(formData.chargingTimeDC))
      : null,
    batteryType: formData.batteryType || null,
    batteryMileageLimit: isNotNil(formData.batteryMileageLimit)
      ? Number(formData.batteryMileageLimit)
      : null,
    batteryWarrantyUntil: formData.batteryWarrantyUntil || null,
    certificateFileId: formData.batteryCertificateDocument?.fileId ?? null,
    batteryOwnershipType: formData.batteryOwnershipType || null,
    chargingConnectorType: formData.chargingConnectorType || null,
    hasRangeExtender: formData.hasRangeExtender ?? null,
    gasRevisionValidUntil: formData.gasRevisionValidUntil || null,
    pressureVesselValidUntil: formData.pressureVesselValidUntil || null,

    // Additional missing integer fields from schema
    cylinderCount: isNotNil(formData.cylinderCount) ? Number(formData.cylinderCount) : null,
    gearCount: isNotNil(formData.gearCount) ? Number(formData.gearCount) : null,
    ownerCount: isNotNil(formData.ownerCount) ? Number(formData.ownerCount) : null,
    primaryKeyCount: isNotNil(formData.primaryKeyCount) ? Number(formData.primaryKeyCount) : null,
    maximalPower: isNotNil(formData.maximalPower) ? Number(formData.maximalPower) : null,

    // Appearance
    exteriorColorSpecification: formData.exteriorColorSpecification?.at(0) || null,
    exteriorColor: formatColor(formData.exteriorColor),
    manufacturerColorCode: formData.manufacturerColorCode || null,
    manufacturerColorName: formData.manufacturerColorName || null,
    interiorMaterial: formData.interiorMaterial?.at(0) || null,
    interiorColor: formatColor(formData.interiorColor),
    interiorColorName: formData.interiorColorName || null,

    // Service Book
    serviceBookType: formData.serviceBookType?.at(0) || null,
    serviceBookState: formData.serviceBookState?.at(0) || null,

    // Technical Inspection
    isAcceptableForTechnicalInspection: formData.isAcceptableForTechnicalInspection ?? null,
    technicalInspectionNote: formData.technicalInspectionNote || null,
    technicalInspectionValidUntil: formData.technicalInspectionValidUntil || null,

    // Service Contract
    serviceContractMileageLimit: formData.serviceContractMileageLimit || null,
    serviceContractNote: formData.serviceContractNote || null,
    serviceContractUntil: formData.serviceContractUntil || null,
    warrantyDate: formData.warrantyDate || null,
    warrantyMileage: formData.warrantyMileage || null,

    // Features - properly implement highlighting and premium detection
    features: mapFeatures(formData.features || undefined, existingFeatures, sourcingData),

    // Condition
    condition: (formData.condition?.at(0) as ConditionEnum) || null,
  };
};

// TODO-SF: I think we might have platform util for this?
/**
 * Helper function to format manufactured date from year/month to ISO string
 */
const formatManufacturedDate = (year?: string | null, month?: string | null): string | null => {
  if (!year) {
    return null;
  }

  const yearNum = parseInt(year, 10);
  if (isNaN(yearNum) || yearNum < 1900) {
    return null;
  }

  if (!month) {
    return year;
  }

  const monthNum = parseInt(month, 10);
  if (isNaN(monthNum) || monthNum < 1 || monthNum > 12) {
    return year;
  }

  return `${yearNum}-${month.toString().padStart(2, '0')}`;
};
